import { useEffect, useRef, useState } from 'react'
import { GEOMETRY_BLOCKED_PULSE_MS } from '@/components/app/editor/selection-chrome/geometry-blocked-feedback'

export interface FrozenOutlineChrome {
  /** Draw the frozen hover outline. */
  showFrozenHoverChrome: boolean
  /** The outline is mid-pulse (add the pulse class). */
  showFrozenOutlinePulse: boolean
  onPointerEnter: () => void
  onPointerLeave: () => void
}

/**
 * Hover-only frozen outline, plus a timed pulse each time `frozenOutlinePulseKey` increments (e.g.
 * a rejected shift+click add).
 *
 * No snowflake badge here — that's reserved for the selected state via SelectionFrameBorder;
 * FROZEN_OBJECT_CURSOR already signals "frozen" on hover. Hidden for a deselected, non-hovered
 * frozen object. This must stay a strict hover/pulse check with no "can't track hover, so always
 * show" fallback: a group descendant whose hits pass through to the parent group's path (out of
 * the active isolation scope) never fires onPointerEnter/Leave here, so a fallback would show the
 * outline unconditionally.
 */
export function useFrozenOutlineChrome(
  frozenOutlinePulseKey: number,
  isSelected: boolean,
  showPersistentFrozenOutline: boolean
): FrozenOutlineChrome {
  const lastConsumedPulseKeyRef = useRef(frozenOutlinePulseKey)
  const [showFrozenOutlinePulse, setShowFrozenOutlinePulse] = useState(false)
  const [isFrozenHovered, setIsFrozenHovered] = useState(false)

  useEffect(() => {
    if (frozenOutlinePulseKey <= lastConsumedPulseKeyRef.current) {
      return
    }

    lastConsumedPulseKeyRef.current = frozenOutlinePulseKey
    setShowFrozenOutlinePulse(true)

    const timer = window.setTimeout(() => {
      setShowFrozenOutlinePulse(false)
    }, GEOMETRY_BLOCKED_PULSE_MS)

    return () => window.clearTimeout(timer)
  }, [frozenOutlinePulseKey])

  return {
    showFrozenHoverChrome:
      !isSelected && showPersistentFrozenOutline && (isFrozenHovered || showFrozenOutlinePulse),
    showFrozenOutlinePulse,
    onPointerEnter: () => setIsFrozenHovered(true),
    onPointerLeave: () => setIsFrozenHovered(false),
  }
}
